import dayjs from 'dayjs'
import useFetchCountries from 'hooks/useFetchCountries/useFetchCountries'
import { CountryContract, CurrencyContract } from 'models'
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react'
import { IIP } from 'Services/api/ip.service'
import useCurrentIP from './hooks/useCurrentIP'
import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import { repository as currencyService } from 'Services/api/currency.service'
import buildRegisterContract from './utils/buildRegisterContract'
import globalService from 'Services/api/global.service'
import posthogIntegration from 'utilities/posthogIntegration'
import { mapCountryToIANATimezoneName } from './utils/mapCountryToTimezone'
import { usePostHog } from 'posthog-js/react'
import toast from 'utilities/toast/toast'
import errorMessages from 'utilities/errorMessages'
import { useTranslation } from 'react-i18next'

export enum CreateCompanyStep {
    AdminAccount,
    RestaurantInfo,
    RestaurantLocation,
    RestaurantSetup,
    Completed,
}

export interface INewAccountContract {
    userName: string
    email: string
    password: string
    pin: string
    firstName: string
    lastName: string
    confirmPassword: string
    confirmPin: string
}

export interface ICreateCompanyAddress {
    countryID: number
    postalCode: string
    province: string
    addressLine1: string
}
export interface IRestaurantInfo {
    name: string
    email: string
    address: ICreateCompanyAddress
}
export interface IRestaurantLocation {
    name: string
    subDomain: string
    address: ICreateCompanyAddress
    locationAddressSameCompany: boolean
}

export interface IRestaurantSetup {
    currencyId: number
    isTaxInclusive: boolean
    recaptchaCode: string
    demoData: 'yes' | 'no'
}
export type TCreateCompanyContext = {
    step: CreateCompanyStep | undefined
    countries: CountryContract[]
    currencies: CurrencyContract[]
    currentIPInfo: IIP | undefined
    onNext: () => void
    onPrev: () => void
    onSubmitAccount: (newAcc: INewAccountContract) => void
    onSubmitRestaurantInfo: (restaurantInfo: IRestaurantInfo) => void
    onSubmitRestaurantLocation: (
        restaurantLocation: IRestaurantLocation,
    ) => void
    onSubmitRestaurantSetup: (restaurantSetup: IRestaurantSetup) => void
    preLoading: boolean
    account: INewAccountContract
    restaurantInfo: IRestaurantInfo
    restaurantLocation: IRestaurantLocation
    restaurantSetup: IRestaurantSetup
    registerLoading: boolean
}

const CreateCompanyContext = createContext<TCreateCompanyContext>(
    {} as TCreateCompanyContext,
)

export const useCreateCompanyContext = () => useContext(CreateCompanyContext)

export default CreateCompanyContext

export function CreateCompanyContextProvider({
    children,
}: {
    children: ReactNode
}) {
    const { t } = useTranslation()
    const [step, setStep] = useState<CreateCompanyStep>()
    const [account, setAccount] = useState<INewAccountContract>(
        // {
        //     firstName: 'nook',
        //     lastName: 'haha',
        //     userName: 'snookpy',
        //     email: 'nookittipon@gmail.com',
        //     password: 'Password123!',
        //     confirmPassword: 'Password123!',
        //     pin: '132546',
        //     confirmPin: '132546',
        // }
        {
            userName: '',
            email: '',
            password: '',
            pin: '',
            firstName: '',
            lastName: '',
            confirmPassword: '',
            confirmPin: '',
        },
    )
    const [restaurantInfo, setRestaurantInfo] = useState<IRestaurantInfo>(
        //     {
        //     name: 'mycossos',
        //     email: 'nook@gmail.com',
        //     address: {
        //         countryID: 219,
        //         postalCode: '50200',
        //         province: 'chaing',
        //         addressLine1: 'ahahahah',
        //     }
        // }
        {
            name: '',
            email: '',
            address: {
                countryID: 0,
                postalCode: '',
                province: '',
                addressLine1: '',
            },
        },
    )

    const [restaurantLocation, setRestaurantLocation] = useState<
        IRestaurantLocation
    >({
        name: '',
        subDomain: '',
        locationAddressSameCompany: true,
        address: {
            countryID: 0,
            postalCode: '',
            province: '',
            addressLine1: '',
        },
    })

    const [restaurantSetup, setRestaurantSetup] = useState<IRestaurantSetup>({
        currencyId: 0,
        isTaxInclusive: false,
        demoData: 'no',
        recaptchaCode: '',
    })

    const { ip: currentIPInfo, ipLoading } = useCurrentIP()

    const [countries, countriesLoading, fetchCountries] = useFetchCountries()

    const [currencies = [], getCurrencies, currenciesStatus] = useFetchApi(
        currencyService.getCurrencies,
    )

    const [, register, registerStatus] = useFetchApi(globalService.register)

    const onNext = useCallback(() => {
        setStep((prevStep) =>
            prevStep !== undefined
                ? prevStep + 1
                : CreateCompanyStep.AdminAccount,
        )
    }, [])

    const onPrev = useCallback(() => {
        setStep((prevStep) => prevStep! - 1)
    }, [])

    useEffect(() => {
        fetchCountries()
        getCurrencies()
    }, [fetchCountries, getCurrencies])

    const onSubmitAccount = useCallback(
        (newAcc: INewAccountContract) => {
            setAccount(newAcc)
            onNext()
        },
        [onNext],
    )

    const onSubmitRestaurantInfo = useCallback(
        (newResInfo: IRestaurantInfo) => {
            setRestaurantInfo(newResInfo)
            onNext()
        },
        [onNext],
    )

    const onSubmitRestaurantLocation = useCallback(
        (newResLocation: IRestaurantLocation) => {
            setRestaurantLocation(newResLocation)
            onNext()
        },
        [onNext],
    )

    const posthog = usePostHog()
    const onSubmitRestaurantSetup = useCallback(
        async (newResSetup: IRestaurantSetup) => {
            setRestaurantSetup(newResSetup)

            const registerInfo = buildRegisterContract({
                account,
                restaurantInfo,
                restaurantLocation,
                restaurantSetup: newResSetup,
                timezoneName:
                    mapCountryToIANATimezoneName(
                        restaurantInfo.address.countryID ?? 0,
                    )?.[0] || dayjs.tz.guess(),
            })

            try {
                await register(registerInfo)
                posthogIntegration.captureRegistration(posthog, registerInfo)
                onNext()
            } catch (error) {
                const { message, arg } = errorMessages(error)
                toast.error(t(message, arg))
                console.log(error)
            }
        },
        [
            account,
            restaurantInfo,
            restaurantLocation,
            register,
            posthog,
            onNext,
            t,
        ],
    )

    useEffect(() => {
        if (currentIPInfo) {
            const countryID = countries.find(
                (c) => c.name === currentIPInfo.country_name,
            )?.countryID
            const province = currentIPInfo?.region ?? ''

            setRestaurantLocation((prev) => ({
                ...prev,
                address: {
                    ...prev.address,
                    countryID: countryID ?? 0,
                    province: province,
                },
            }))

            setRestaurantInfo((prev) => ({
                ...prev,
                address: {
                    ...prev.address,
                    province: province,
                    countryID: countryID ?? 0,
                },
            }))
        }
    }, [currentIPInfo, countries])

    return (
        <CreateCompanyContext.Provider
            value={{
                step,
                onNext,
                onPrev,
                onSubmitAccount,
                account,
                restaurantInfo,
                restaurantLocation,
                onSubmitRestaurantInfo,
                onSubmitRestaurantLocation,
                onSubmitRestaurantSetup,
                preLoading:
                    countriesLoading ||
                    ipLoading ||
                    isLoading(currenciesStatus),
                countries,
                currencies,
                currentIPInfo,
                restaurantSetup,
                registerLoading: isLoading(registerStatus),
            }}
        >
            {children}
        </CreateCompanyContext.Provider>
    )
}
